import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const SocialContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, #faf8f5 0%, #f5f0eb 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 300;
  color: #d4737d;
  margin: 0 0 1rem 0;
  font-family: 'Playfair Display', serif;
  letter-spacing: -0.02em;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: clamp(1.2rem, 2.5vw, 1.8rem);
  line-height: 1.6;
  color: #8b6f7d;
  margin: 0 0 4rem 0;
  font-family: 'Cormorant Garamond', serif;
  text-align: center;
  max-width: 800px;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 4rem;
  margin-bottom: 4rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  min-width: 150px;
`;

const StatNumber = styled.div`
  font-size: clamp(2.5rem, 4vw, 4rem);
  font-weight: 600;
  color: #d4737d;
  font-family: 'Playfair Display', serif;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: #666;
  font-family: 'Cormorant Garamond', serif;
  margin-top: 0.5rem;
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const PostCard = styled(motion.div)`
  background: white;
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const PostImage = styled.div`
  width: 100%;
  /* allow the media to scale without cropping */
  min-height: 340px;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, #d4737d 0%, #b85862 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  @media (max-width: 768px) {
    min-height: 220px;
    aspect-ratio: auto;
  }
`;

const ViralBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Playfair Display', serif;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
`;

const PostContent = styled.div`
  padding: 2rem;
`;

const PostTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 500;
  color: #333;
  margin: 0 0 1rem 0;
  font-family: 'Playfair Display', serif;
`;

const PostStats = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
`;

const PostStat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #d4737d;
  font-weight: 500;
  font-family: 'Cormorant Garamond', serif;
`;

const PostDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  margin: 0;
  font-family: 'Cormorant Garamond', serif;
`;

const PlatformTag = styled.span`
  background: ${props => props.platform === 'tiktok' ? '#000' : props.platform === 'instagram' ? 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)' : '#1877f2'};
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 1rem;
  display: inline-block;
  font-family: 'Playfair Display', serif;
`;

const SocialMedia = () => {
  const stats = [
    { number: "~1.8M", label: "Total Views" },
    { number: "~240k", label: "Total Likes" },
    { number: "85%", label: "Engagement Rate" }
  ];

  const posts = [
    {
      title: "F1 Amsterdam",
      platform: "tiktok",
      views: "1.7M",
      likes: "240k",
      description: "Going viral with authentic behind-the-scenes content that shows F1 history.",
      isViral: true,
      mediaType: 'video',
      src: '/sm/f1.mov'
    },
    {
      title: "Restaurant in Utrecht",
      platform: "tiktok", 
      views: "14k",
      likes: "400+",
      description: "Showcasing the vibrant atmosphere and exquisite dishes through captivating visuals.",
      isViral: false,
      mediaType: 'video',
      src: '/sm/utrecht.mov'
    },
    {
      title: "South Korea Haul",
      platform: "tiktok",
      views: "15K",
      likes: "500+", 
      description: "Sharing my latest finds from South Korea, blending fashion and culture in a fun, engaging way.",
      isViral: false,
      mediaType: 'carousel',
      images: [
        '/sm/post3/1.jpeg',
        '/sm/post3/2.jpeg',
        '/sm/post3/3.jpeg',
        '/sm/post3/4.jpeg',
        '/sm/post3/5.jpeg',
        '/sm/post3/6.jpeg',
        '/sm/post3/7.jpeg'
      ]
    }
  ];

  // Small carousel used for the third post
  const PostCarousel = ({ images }) => {
    const [active, setActive] = useState(0);
    const prev = () => setActive((active - 1 + images.length) % images.length);
    const next = () => setActive((active + 1) % images.length);

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', minHeight: 340 }}>
        <img
          src={images[active]}
          alt={`Post ${active + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
        <button onClick={prev} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer' }}>◀</button>
        <button onClick={next} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer' }}>▶</button>
      </div>
    );
  };

  return (
    <SocialContainer id="social">
      <Title>Social Media</Title>
      <Subtitle>
        Creating viral moments since March 2025, and building authentic connections across platforms. 
        See how in the short time since then, I've grown my audience and increased engagement.
      </Subtitle>
      
      <StatsContainer>
        {stats.map((stat, index) => (
          <StatItem
            key={index}
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <StatNumber>{stat.number}</StatNumber>
            <StatLabel>{stat.label}</StatLabel>
          </StatItem>
        ))}
      </StatsContainer>

      <PostsGrid>
        {posts.map((post, index) => (
          <PostCard
            key={index}
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <PostImage>
                {post.isViral && <ViralBadge>🔥 VIRAL</ViralBadge>}
                {post.mediaType === 'video' && (
                  <video src={post.src} controls playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}

                {post.mediaType === 'carousel' && (
                  <PostCarousel images={post.images} />
                )}
              </PostImage>
            <PostContent>
              <PostTitle>{post.title}</PostTitle>
              <PostStats>
                <PostStat>👁 {post.views}</PostStat>
                <PostStat>❤️ {post.likes}</PostStat>
              </PostStats>
              <PostDescription>{post.description}</PostDescription>
              <PlatformTag platform={post.platform}>
                {post.platform.toUpperCase()}
              </PlatformTag>
            </PostContent>
          </PostCard>
        ))}
      </PostsGrid>
    </SocialContainer>
  );
};

export default SocialMedia;